import React from 'react';
import BaseChart from './BaseChart';
import { EChartsOption } from 'echarts';

interface MultiLineChartProps {
  data: {
    name: string;
    data: number[];
    color?: string;
  }[];
  xAxisData: string[];
  title?: string;
  className?: string;
  loading?: boolean;
  yAxisFormatter?: (value: number) => string;
  showArea?: boolean;
}

const MultiLineChart: React.FC<MultiLineChartProps> = ({
  data,
  xAxisData,
  title,
  className,
  loading = false,
  yAxisFormatter,
  showArea = false
}) => {
  const colors = ['#10B981', '#3B82F6', '#8B5CF6', '#F59E0B', '#EF4444'];

  const option: EChartsOption = {
    title: {
      text: title,
      textStyle: {
        color: '#1f2937',
        fontSize: 16
      },
      top: 10,
      left: 10
    },
    tooltip: {
      trigger: 'axis',
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      textStyle: {
        color: '#1f2937'
      },
      formatter: function(params: any) {
        let result = `${params[0].axisValue}<br/>`;
        params.forEach((param: any) => {
          result += `${param.marker}${param.seriesName}: ${param.value}${yAxisFormatter ? '' : ''}<br/>`;
        });
        return result;
      }
    },
    legend: {
      data: data.map(item => item.name),
      textStyle: {
        color: '#1f2937'
      },
      top: 35,
      left: 10
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '3%',
      top: '20%',
      containLabel: true
    },
    xAxis: {
      type: 'category',
      boundaryGap: false,
      data: xAxisData,
      axisLine: {
        lineStyle: {
          color: '#d1d5db'
        }
      },
      axisLabel: {
        color: '#6b7280'
      }
    },
    yAxis: {
      type: 'value',
      axisLine: {
        lineStyle: {
          color: '#d1d5db'
        }
      },
      axisLabel: {
        color: '#6b7280',
        formatter: yAxisFormatter || function(value: number) {
          return value + '%';
        }
      },
      splitLine: {
        lineStyle: {
          color: '#d1d5db'
        }
      }
    },
    series: data.map((item, index) => ({
      name: item.name,
      type: 'line',
      data: item.data,
      smooth: true,
      symbol: 'circle',
      symbolSize: 6,
      lineStyle: {
        width: 3,
        color: item.color || colors[index % colors.length]
      },
      itemStyle: {
        color: item.color || colors[index % colors.length],
        borderColor: '#ffffff',
        borderWidth: 2
      },
      areaStyle: showArea ? {
        color: {
          type: 'linear',
          x: 0,
          y: 0,
          x2: 0,
          y2: 1,
          colorStops: [{
            offset: 0,
            color: item.color || colors[index % colors.length]
          }, {
            offset: 1,
            color: 'transparent'
          }]
        },
        opacity: 0.3
      } : undefined
    }))
  };

  return (
    <BaseChart
      option={option}
      className={className}
      loading={loading}
    />
  );
};

export default MultiLineChart;